import { ColumnMeta } from '@dealstack/frontend/app/pages/Valuations/components/table/columnBuilder/columnBuilder.types';
import { Column, ColumnDef, RowData, Table } from '@tanstack/react-table';

/**
 * Gets column data with custom type metadata from a TanStack Table column
 *
 * This function retrieves the metadata from the column and casts it to use our custom
 * column metadata type (ColumnMeta). This ensures type safety when accessing
 * custom properties like subColumns, isSubColumn, etc.
 *
 * @param column - The TanStack Table instance
 * @returns The column custom metadata with typed properties
 *
 * @example
 * ```tsx
 * const columnMeta = getColumnMeta(column); //typed as ColumnMeta
 * if (columnMeta.subColumns) {
 *   // TypeScript knows this is an array of subcolumn IDs
 *   meta.subColumns.forEach(subId => console.log(subId));
 * }
 * ```
 */
export function getColumnMeta<TData extends RowData, TValue = unknown>(
	column: Column<TData, TValue>
): ColumnMeta<TData, TValue> {
	return getColumnDefMeta(column.columnDef);
}

export function getColumnDefMeta<TData extends RowData, TValue = unknown>(
	columnDef: ColumnDef<TData, TValue>
): ColumnMeta<TData, TValue> {
	return columnDef.meta as ColumnMeta<TData, TValue>;
}
/**
 * Gets a column with custom type metadata from a TanStack Table
 *
 * This function retrieves a column from the table by its ID and casts it to include
 * our custom column metadata types. This ensures type safety when accessing
 * custom properties and provides a consistent way to get typed columns.
 *
 * @param table - The TanStack Table instance
 * @param columnId - The unique identifier of the column to retrieve
 * @returns The column with custom metadata types applied
 *
 * @example
 * ```tsx
 * const column = getColumnWithMeta(table, 'proceeds');
 * const meta = column.columnDef.meta; // Now typed as ColumnMeta
 * if (meta.subColumns) {
 *   // TypeScript knows this is an array of subcolumn IDs
 *   meta.subColumns.forEach(subId => console.log(subId));
 * }
 * ```
 */
export function getColumnWithMeta<TData extends RowData, TValue = unknown>(
	table: Table<TData>,
	columnId: string
): (Column<TData, TValue> & { columnDef: { meta: ColumnMeta<TData, TValue> } }) | undefined {
	const column = table.getColumn(columnId);

	if (!column) {
		return undefined;
	}

	return column as Column<TData, TValue> & { columnDef: { meta: ColumnMeta<TData, TValue> } };
}
